import { type ComponentProps, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import * as yup from 'yup';

import { isSortable } from '@dnd-kit/dom/sortable';
import { DragDropProvider } from '@dnd-kit/react';
import { useSortable } from '@dnd-kit/react/sortable';

import Button from 'components/shared/Button';
import DragHandle from 'components/shared/DragHandle';
import FieldLabel from 'components/shared/form/FieldLabel';
import Input from 'components/shared/form/Input';
import CostTierEmptyState from 'components/shared/highCostMeds/CostTierEmptyState';
import { FormProvider, useForm } from 'context/form';
import { FormValues } from 'context/form/types';
import { HighCostMedicationTier } from 'models/HighCostMedicationList';
import { BodySmall, BodySmallBold } from 'styles/typography';

// What one save carries: every tier the list owns, each with the description it now holds and the
// 0-based position it now sits at. The reorder submits the whole key rather than a move, so the
// server stores what it is handed and never has to interpret one. See design 002, "The reorder
// submits the whole key, not a move".
export type CostTierUpdate = {
  id: string;
  description: string | null;
  position: number;
};

type CostTierFormValues = {
  // Tier ids in display order. Held as a form value rather than beside the form so a drag counts
  // towards `dirty` like any edit, and cancelling discards a rearrangement for free.
  order: string[];
  // Keyed by tier id rather than by field name: which tiers a list carries is data, not something
  // known at compile time.
  descriptions: Record<string, string>;
};

// Every field is optional free-text prose, so there is nothing to validate. Design 001 is explicit
// that descriptions carry no structured bounds: the AI consumer reads prose, and not every pharmacy
// contract states dollar ranges at all.
const costTierSchema = yup.object();

type DragEndHandler = NonNullable<ComponentProps<typeof DragDropProvider>['onDragEnd']>;

export type CostTierFormProps = {
  tiers: HighCostMedicationTier[];
  isSaving: boolean;
  onSave: (updates: CostTierUpdate[]) => void;
  onCancel: () => void;
  // The admin slideout replaces the panel body with this form and has no heading of its own, so the
  // form supplies one. The customer modal's header already says Edit Cost Tiers Key, so it turns
  // this off rather than stacking two headings. The explainer copy stays either way — it is what
  // tells a reader the order is display-only.
  showTitle?: boolean;
};

// The cost tier key form. Labels are read-only and there is no add or remove affordance: the key is a
// rollup of what the medications carry, so tiers change by editing the medications themselves. What
// is authorable is each tier's description and the order they display in (design 001).
//
// Shared deliberately: the admin slideout and the customer High Cost Meds page render this same
// component, so the copy and the drag behavior cannot drift between the two surfaces. Each caller
// owns its own request — hence `onSave` rather than a mutation in here.
export default function CostTierForm({ tiers, isSaving, onSave, onCancel, showTitle = true }: CostTierFormProps) {
  const handleSubmit = (values: FormValues<CostTierFormValues>) => {
    const order = values.order ?? [];
    const descriptions = values.descriptions ?? {};

    // Positions are the array's own indices, so the key the server receives is always dense and
    // 0-based no matter how the rows were dragged around to get here.
    onSave(
      order.map((id, index) => ({
        id,
        description: (descriptions[id] ?? '').trim() || null,
        position: index,
      }))
    );
  };

  if (tiers.length === 0) return <CostTierEmptyState onCancel={onCancel} />;

  return (
    <FormProvider<CostTierFormValues, object>
      onSubmit={handleSubmit}
      yupSchema={costTierSchema}
      initialValues={{
        order: tiers.map((tier) => tier.id),
        descriptions: Object.fromEntries(tiers.map((tier) => [tier.id, tier.description ?? ''])),
      }}>
      <CostTierFields tiers={tiers} isSaving={isSaving} onCancel={onCancel} showTitle={showTitle} />
    </FormProvider>
  );
}

function CostTierFields({ tiers, isSaving, onCancel, showTitle }: Omit<CostTierFormProps, 'onSave'>) {
  const { values, setValue, dirty, submitForm } = useForm<CostTierFormValues, object>();

  // Memoised because handleDragEnd depends on it, and a fresh `[]` fallback every render would
  // re-create the handler on every render with it.
  const order = useMemo(() => values.order ?? [], [values.order]);
  const descriptions = values.descriptions ?? {};

  // Rows render from the staged order, so turning that order back into tiers needs a lookup by id.
  const tiersById = useMemo(() => new Map(tiers.map((tier) => [tier.id, tier])), [tiers]);

  // Lifted from CategoriesList#handleDragEnd — splice the row out, splice it back in at its new
  // index, and let the array's indices become the positions on save.
  const handleDragEnd = useCallback(
    (...[event]: Parameters<DragEndHandler>) => {
      if (event.canceled) return;

      const { source } = event.operation;

      if (!source || !isSortable(source)) return;

      const oldIndex = source.initialIndex;
      const newIndex = source.index;

      if (oldIndex === newIndex) return;

      const reordered = [...order];
      const [moved] = reordered.splice(oldIndex, 1);

      reordered.splice(newIndex, 0, moved);

      setValue('order', reordered);
    },
    [order, setValue]
  );

  return (
    <Container>
      {showTitle && <SectionTitle>Edit Cost Tiers</SectionTitle>}
      <Description>
        Describe what each tier costs in this contract&rsquo;s own terms. Descriptions render verbatim in the cost tier
        key, wherever a tier is shown. Changing the order updates how the cost tier key is displayed.
      </Description>

      <DragDropProvider onDragEnd={handleDragEnd}>
        <Fields>
          {order.map((id, index) => {
            const tier = tiersById.get(id);

            if (!tier) return null;

            return (
              <SortableTierField
                key={id}
                tier={tier}
                index={index}
                value={descriptions[id] ?? ''}
                disabled={isSaving}
                onChange={(description) => setValue('descriptions', { ...descriptions, [id]: description })}
              />
            );
          })}
        </Fields>
      </DragDropProvider>

      <Actions>
        <Button variant='ghost' onClick={onCancel} disabled={isSaving}>
          Cancel
        </Button>
        <Button variant='primary' onClick={() => submitForm()} loading={isSaving} disabled={!dirty || isSaving}>
          Confirm
        </Button>
      </Actions>
    </Container>
  );
}

function SortableTierField({
  tier,
  index,
  value,
  disabled,
  onChange,
}: {
  tier: HighCostMedicationTier;
  index: number;
  value: string;
  disabled: boolean;
  onChange: (description: string) => void;
}) {
  const { ref, handleRef, isDragSource } = useSortable({ id: tier.id, index });

  return (
    <Field ref={ref} $lifted={isDragSource}>
      {/* Read-only on purpose. The label is the pharmacy contract's own vocabulary and belongs to
          whichever medications carry it, so renaming it here would retitle it for every one of
          them. Tiers are renamed by editing those medications. */}
      <LabelCell>
        <FieldLabel>{tier.label}</FieldLabel>
      </LabelCell>
      {/* A dedicated grip rather than a draggable row body, as on the admissions criteria rows — the
          row holds a text input, and a draggable body would swallow the click that focuses it. */}
      <HandleCell>
        <DragHandle ref={handleRef} data-testid={`tier-drag-handle-${tier.id}`} />
      </HandleCell>
      <InputCell>
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder='Description'
          disabled={disabled}
          data-testid={`tier-description-${tier.id}`}
        />
      </InputCell>
    </Field>
  );
}

// The form replaces the table directly under the panel header, and the panel body carries no top
// padding (the list view gets its spacing from the toolbar), so the gap is set here. Mirrors
// HighCostMedListEntryForm.
const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding-top: 24px;
`;

const SectionTitle = styled(BodySmallBold)`
  font-size: 16px;
  margin-bottom: 8px;
`;

const Description = styled(BodySmall)`
  display: block;
  margin-bottom: 24px;
`;

const Fields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 40px;
`;

// A grid rather than nested flex rows so the grip centres on the input without a hand-tuned offset:
// the label spans the full width on row 1, and the grip and input share row 2.
const Field = styled.div<{ $lifted: boolean }>`
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: center;
  column-gap: 8px;
  opacity: ${({ $lifted }) => ($lifted ? 0.5 : 1)};
`;

const LabelCell = styled.div`
  grid-area: 1 / 2;
`;

// Centres in row 2, which holds the input alone — so the grip lines up with the input rather than
// with the label above it, without a hand-tuned offset.
const HandleCell = styled.div`
  grid-area: 2 / 1;
  display: flex;
  align-items: center;
`;

const InputCell = styled.div`
  grid-area: 2 / 2;
  min-width: 0;
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 16px;
`;
